<template>
  <footer v-if="url && title" class="text-white grid grid-cols-12">
    <section class="row-start-1 col-span-full relative pt-20 container z-grid v-page-section-container-padding">
      <div class="col-span-full sm:col-span-10 md:col-span-8 xl:col-span-6 2xl:col-span-4 row-start-1 pr-6 md:pr-16 2xl:pr-0">
        <p class="md:text-lg lg:text-xl leading-none lg:font-medium mb-1 lg:mb-3 order-2">{{ label }}</p>
        <h2 v-if="title" class="font-semibold leading-tight text-xl md:text-2xl lg:text-3xl pr-12 mb-4 order-1">{{ title }}</h2>
        <div class="pr-20 sm:pr-32 s-formkit s-formkit--reset order-3">
          <FormKit v-model="newsletterSubscriptionForm" type="form" :actions="false" @submit="handleNewsletterSubscription">
            <FormKit
              name="emailAdress"
              type="email"
              label="E-mailadres"
              validation="bail|required|email"
              validation-visibility="blur"
              :validation-messages="{
                required: t_('validation-missing-email'),
                email: t_('validation-invalid-email'),
              }"
              :placeholder="t_('input-placeholder')"
              class="w-full mb-4 lg:mb-8"
            />
            <FormKit type="submit" :classes="{ outer: 'submit-button' }">
              <!-- fallback-component="span": FormKit's submit already renders a <button>; a <button>
                   can't contain another <button> (invalid HTML, causes hydration mismatches). -->
              <MoleculeButton fallback-component="span" size="large" background-color="bg-white hover:bg-black" text-color="text-black hover:text-white">
                {{ t_('button-text') }}
              </MoleculeButton>
            </FormKit>
          </FormKit>
        </div>
      </div>
    </section>
  </footer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { newsletterSubscribe } from '~/assets/newsletter'

const props = defineProps({
  label: { type: String, required: false, default: undefined },
  title: { type: String, required: true },
  url: { type: String, required: true },
  origin: { type: String, required: false, default: 'HomePage' },
})

const config = useRuntimeConfig()
const { locale } = useI18n()
const { t_ } = setupTranslationPrefixer('Newsletter')
const newsletterSubscriptionForm = ref({ emailAdress: '' })
const handleNewsletterSubscription = async () => {
  const emailAddress = unref(newsletterSubscriptionForm.value).emailAdress
  const recaptchaToken = await getRecaptchaToken(config.public.recaptchaSiteKey)
  if (await newsletterSubscribe(config.public.bridgeSiteUrl, locale.value, emailAddress, recaptchaToken, props.origin)) {
    window.location.href = props.url
  }
}
</script>

<style lang="scss" scoped>
.s-formkit.s-formkit--reset {
  :deep(.formkit-outer) {
    .formkit-inner {
      @apply max-w-none mb-0;
      width: 100%;
      // Genesis fakes .formkit-inner's own border via box-shadow (--fk-border-box-shadow), which boxes the
      // whole field regardless of the underline-only border set on .formkit-input below - drop it so only
      // that bottom border shows.
      box-shadow: none;

      .formkit-input {
        @apply p-2 bg-transparent border-solid border-white rounded-none border-b leading-none font-medium font-sans border-l-0 border-r-0 border-t-0 max-w-none;

        @screen md {
          @apply text-lg;
        }

        @screen lg {
          @apply text-xl;
        }

        &::placeholder {
          @apply text-white;
        }

        &:focus-visible {
          @apply rounded;
          outline: -webkit-focus-ring-color inset 2px;
          border-radius: 4px;
        }
      }
    }

    .formkit-messages {
      @apply bg-app-primary-1-red--fire-brick text-white p-2 -mt-px;

      .formkit-message {
        @apply text-sm font-sans font-medium leading-none text-white italic;

        &:last-child {
          @apply mb-0;
        }
      }
    }
  }

  // Resets the global pill-shaped submit button (assets/scss/formkit/_overrides.scss) so only the
  // MoleculeButton passed as this FormKit submit's default slot content is visible (matching Formulate's
  // equivalent reset here).
  :deep(.submit-button .formkit-input) {
    @apply rounded-none p-0 bg-transparent border-0 shadow-none leading-none max-w-none w-full;

    &:hover {
      @apply shadow-none;
    }
  }
}

.submit-button {
  width: 50%;
}
</style>
